"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

interface AccountMenuProps {
  name: string | null;
  email: string;
  role: string;
  signOutAction: () => Promise<void>;
}

/**
 * Top-right identity menu. Replaces the account block that sat at the bottom of
 * the sidebar (and its collapsed-rail popover), so sign-out and My Account are
 * reachable from the same place on every page.
 */
export function AccountMenu({ name, email, role, signOutAction }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const initials = (name ?? email).slice(0, 2).toUpperCase();

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account"
        title={name ?? email}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-spark-green to-spark-blue text-xs font-bold text-spark-ink ring-offset-2 ring-offset-spark-black transition-shadow hover:ring-2 hover:ring-white/25"
      >
        {initials}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-[70] mt-2 w-64 rounded-lg border border-white/10 bg-[#1C1F26] p-3 shadow-[0_12px_32px_rgba(0,0,0,.5)]"
        >
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-spark-green to-spark-blue text-xs font-bold text-spark-ink">
              {initials}
            </div>
            <div className="min-w-0">
              {name && <p className="truncate text-xs font-medium text-white">{name}</p>}
              <p className="truncate text-xs text-[#AAB2BD]">{email}</p>
            </div>
          </div>
          <span className="mt-2 inline-block rounded-md bg-white/[.08] px-2 py-0.5 text-xs font-medium capitalize text-[#AAB2BD]">
            {role}
          </span>

          <div className="my-3 border-t border-white/[.08]" />

          <Link
            href="/account"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-[#AAB2BD] transition-colors hover:bg-white/[.05] hover:text-white"
          >
            <svg
              width={15}
              height={15}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="shrink-0"
              aria-hidden
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            My Account
          </Link>

          <form action={signOutAction} className="mt-2">
            <button
              type="submit"
              role="menuitem"
              className="w-full rounded-md border border-white/[.12] px-3 py-1.5 text-xs font-medium text-[#AAB2BD] transition-colors hover:bg-white/[.05] hover:text-white"
            >
              Sign out
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
